import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import { SortableTh, useSortedRows } from '../components/DataTable';
import usePermissions from '../auth/usePermissions';
import usePlantScope from '../auth/usePlantScope';
import useIsMobile from '../auth/useIsMobile';
import { deletePlant, getPlantByCode, updatePlant } from '../services/plants';
import {
  assignmentHasEnded,
  assignmentsAtPlant,
  getSuppliersByCodes,
  listSuppliersForPlant,
  serviceLabel,
} from '../services/suppliers';
import { listPartsForPlant } from '../services/parts';
import { listTeam } from '../services/team';
import { logContactAddedEvent } from '../services/salesTracker';
import PlantForm from './PlantForm';
import { savePlantPdf } from '../utils/plantPdf';
import { formatAddressLine, formatPhone } from '../utils/formatters';
import { displayFullName } from '../utils/teamDisplay';

/**
 * Plant detail page. For now this is a read-friendly placeholder that shows
 * the core identifying info + any free-text sections, plus an Edit button
 * that reopens the same PlantForm used on the index page.
 *
 * The richer, PDF-mirroring layout (crew shifts table, PPE grid, contacts
 * cards, parts list, etc.) comes next turn — this page exists now so that
 * row clicks from /plants don't dead-end.
 */
export default function PlantDetail() {
  const { plantCode } = useParams();
  const { canEditPlants, canDeletePlants, name, email } = usePermissions();
  // Regional / Liaison Managers may only open plants they're assigned to.
  const { scoped, loading: scopeLoading, allows } = usePlantScope();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [plant, setPlant] = useState(null);
  const [parts, setParts] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suppliersByCode, setSuppliersByCode] = useState({});
  // Suppliers that have a direct plant assignment for this plant (i.e.
  // they appear in SupplierPlantsJson with this plantCode) — separate
  // from the parts-derived list since a supplier can be on a plant
  // without having any parts there yet.
  const [directSuppliers, setDirectSuppliers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await getPlantByCode(plantCode);
      setPlant(p);
      // Load parts for this plant first — the supplier list is derived from
      // Parts' SupplierCode column, not from anything on the plant itself.
      let partsRows = [];
      if (p?.PlantCode) {
        try {
          partsRows = await listPartsForPlant(p.PlantCode);
        } catch {
          partsRows = [];
        }
      }
      setParts(partsRows);
      // Derive unique supplier codes across all parts that ship to this plant,
      // then fetch the supplier records so we can show names + link chips.
      const supplierCodes = Array.from(
        new Set(partsRows.map((pt) => pt.SupplierCode).filter(Boolean)),
      );
      if (supplierCodes.length) {
        try {
          const map = await getSuppliersByCodes(supplierCodes);
          setSuppliersByCode(map);
        } catch {
          setSuppliersByCode({});
        }
      } else {
        setSuppliersByCode({});
      }
      // Also pull suppliers that declared this plant directly in their
      // Plant Locations & Coverage table — those belong on the plant's
      // Suppliers list even if no parts have been linked yet.
      if (p?.PlantCode) {
        try {
          const direct = await listSuppliersForPlant(p.PlantCode);
          setDirectSuppliers(direct || []);
        } catch {
          setDirectSuppliers([]);
        }
      } else {
        setDirectSuppliers([]);
      }
      // Pull the team so we can auto-derive this plant's Regional Manager,
      // Liaison Managers, and Liaisons (anyone with this plant code in their
      // plantCodes) and link them to their team-member detail pages.
      try {
        const rows = await listTeam();
        setTeam(rows || []);
      } catch {
        setTeam([]);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [plantCode]);

  useEffect(() => {
    load();
  }, [load]);

  // Delete this plant. Director-only: the button is hidden for everyone
  // else AND the handler re-checks the flag, so a stray call can't slip
  // through. Plain window.confirm matches the Supplier / Team Member /
  // Part delete pattern. The confirm spells out what is left behind —
  // parts, supplier assignments and team plant codes still point at this
  // code after the row is gone.
  const onDelete = async () => {
    if (!canDeletePlants) return;
    const partCount = (parts || []).length;
    const supplierCount = (directSuppliers || []).length;
    const linked = [];
    if (partCount) linked.push(`${partCount} part${partCount === 1 ? '' : 's'}`);
    if (supplierCount) {
      linked.push(`${supplierCount} supplier${supplierCount === 1 ? '' : 's'}`);
    }
    const warning = linked.length
      ? ` ${linked.join(' and ')} still reference ${plant.PlantCode} and will not be removed.`
      : '';
    if (
      !window.confirm(
        `Delete ${plant.Title} (${plant.PlantCode})? This can't be undone.${warning}`,
      )
    ) {
      return;
    }
    try {
      await deletePlant(plant.id);
      navigate('/plants');
    } catch (err) {
      console.error('[PlantDetail] Delete failed:', err);
      window.alert('Could not delete this plant. Check the browser console for details.');
    }
  };

  const onSave = async (model) => {
    setSaving(true);
    setSaveError(null);
    try {
      // Snapshot the pre-save contacts so we can diff against the new
      // model after the write — powers the Dashboard "Contact Changes"
      // feed.
      const oldContacts = Array.isArray(plant.plantContacts)
        ? plant.plantContacts.map((c) => ({ ...c }))
        : [];
      await updatePlant(plant.id, model);
      setEditing(false);
      // Log new contact rows — additions only. Fire-and-forget so a log
      // write failure can't block the reload or surface a save error.
      try {
        const newContacts = diffAddedContacts(
          oldContacts,
          model.plantContacts || [],
        );
        await Promise.all(
          newContacts.map((c) =>
            logContactAddedEvent({
              parentType: 'Plant',
              parentCode: plant.PlantCode,
              parentName: plant.Title,
              contactName: c.name || '',
              contactTitle: c.title || '',
              postedBy: name || email || '',
            }).catch((err) => {
              // eslint-disable-next-line no-console
              console.warn('[plant] contact log write failed', err);
            }),
          ),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[plant] contact diff failed', err);
      }
      await load();
    } catch (err) {
      // Most common cause: a column (like PlantClosuresJson or one of
      // the per-section JSON columns) hasn't been provisioned on the
      // SharePoint list yet. Surface the message so the user sees why
      // the save failed instead of the modal silently sitting there.
      console.error('[PlantDetail] save failed', err);
      setSaveError(
        err?.body?.error?.message ||
          err?.message ||
          'Something went wrong saving this plant. Check the browser console for details.',
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- Derive the plant's Proxemics team from the team roster -----------
  // Anyone who has this plant in their `plantCodes` and is currently Active
  // or On Leave counts. Split by PortalRole so the card can show RMs, LMs,
  // and individual Liaisons separately — and link each to their profile.
  //
  // The COO (and any other C-suite leader whose Role contains "COO" /
  // "Chief Operating Officer") sits above the per-plant assignments: they
  // oversee every plant by definition, so we surface them on every plant
  // page without requiring a manual plantCodes checkbox. Matched by the
  // Role free-text field so Jillian doesn't have to touch a schema —
  // setting someone's Role to "COO" on their team record is enough to
  // pin them to the top of every plant's org structure.
  const plantTeam = useMemo(() => {
    const code = plant?.PlantCode;
    if (!code) return { coos: [], rms: [], lms: [], liaisons: [] };
    const ACTIVE = new Set(['Active', 'On Leave']);
    const assigned = (team || []).filter(
      (m) =>
        ACTIVE.has(m.EmploymentStatus) &&
        Array.isArray(m.plantCodes) &&
        m.plantCodes.includes(code),
    );
    const byLastName = (a, b) =>
      (a.LastName || '').localeCompare(b.LastName || '') ||
      (a.FirstName || '').localeCompare(b.FirstName || '');
    // COOs come from the FULL active roster, not just members assigned to
    // this plant — that's the whole point of this tier. \b anchors mean
    // a Role like "VP, COO of Ops" still matches while "scoop" wouldn't.
    const isCoo = (m) =>
      /\bcoo\b/i.test(m.Role || '') ||
      /chief operating officer/i.test(m.Role || '');
    const coos = (team || [])
      .filter((m) => ACTIVE.has(m.EmploymentStatus) && isCoo(m))
      .sort(byLastName);
    return {
      coos,
      rms: assigned.filter((m) => m.PortalRole === 'RegionalManager').sort(byLastName),
      lms: assigned.filter((m) => m.PortalRole === 'LiaisonManager').sort(byLastName),
      liaisons: assigned.filter((m) => m.PortalRole === 'Liaison').sort(byLastName),
    };
  }, [plant?.PlantCode, team]);

  // ---- Active supplier totals (rendered as the two stat boxes above
  // the Suppliers card) -------------------------------------------------
  //
  // Walks every supplier that has a per-plant assignment for THIS plant
  // (the same merged set the SuppliersSortableTable below renders) and
  // keeps only the rows whose coverageStatus is "Active". Each kept row
  // contributes:
  //
  //   • +1 to the supplier count
  //   • its hours-allocated, normalised to a per-day equivalent so the
  //     second box can read as a single comparable number regardless of
  //     whether the supplier captured hours weekly, monthly, etc.
  //
  // Per-day normalisation rules:
  //   Per Day               → as-is
  //   Per Shift             → hrs × shifts-per-day at this plant
  //                           (a supplier covering every shift contributes
  //                           hrs to the daily total for each shift)
  //   Per Week              → hrs / coverage-days-count (5/6/7)
  //   Per Month             → hrs / (coverage-days-count × 4.33 weeks)
  //   Other / unknown       → best-effort, treat as already per-day
  //
  // shifts-per-day comes from plant.ShiftsPerDay; if that's empty we fall
  // back to plant.crewShifts.length, and finally to 1.
  //
  // days-per-week comes from the PLANT's own schedule: weekdays (5) plus
  // each weekend day the plant runs shifts. The plant's schedule is the
  // single source of truth for coverage days (the per-supplier
  // coverageDays field was retired, Sep 2026).
  const activeSupplierStats = useMemo(() => {
    const plantCode = String(plant?.PlantCode || '').toUpperCase();
    if (!plantCode) return { count: 0, hoursPerDay: 0 };

    const partsCodes = (parts || []).map((pt) => pt.SupplierCode).filter(Boolean);
    const directCodes = (directSuppliers || []).map((s) => s.SupplierCode).filter(Boolean);
    const mergedCodes = Array.from(new Set([...partsCodes, ...directCodes]));

    const lookup = { ...(suppliersByCode || {}) };
    for (const s of directSuppliers || []) {
      if (s.SupplierCode && !lookup[s.SupplierCode]) lookup[s.SupplierCode] = s;
    }

    const plantDaysPerWeek =
      5 +
      (Number(plant?.ShiftsSaturdays) > 0 ? 1 : 0) +
      (Number(plant?.ShiftsSundays) > 0 ? 1 : 0);
    const shiftsPerDay = (() => {
      // Prefer the new weekday count — most "per shift" supplier hours
      // are weekday-based. Fall back to the legacy ShiftsPerDay number
      // (for plants whose data pre-dates the split), then to the
      // crewShifts row count, and finally to 1 so the math never
      // collapses to zero.
      const weekday = Number(plant?.ShiftsWeekdays);
      if (Number.isFinite(weekday) && weekday > 0) return weekday;
      const legacy = Number(plant?.ShiftsPerDay);
      if (Number.isFinite(legacy) && legacy > 0) return legacy;
      const fromCrew = Array.isArray(plant?.crewShifts)
        ? plant.crewShifts.length
        : 0;
      return fromCrew > 0 ? fromCrew : 1;
    })();

    let count = 0;
    let hoursPerDay = 0;
    for (const code of mergedCodes) {
      const supplier = lookup[code];
      // A supplier may hold several rows at this plant, one per service
      // (Sep 2026). Every Active row contributes its hours; the supplier
      // itself is counted once.
      // Ended assignments (end date in the past) no longer count toward
      // active coverage, whatever their status says (Sep 2026).
      const active = assignmentsAtPlant(supplier, plantCode).filter(
        (a) => (a.coverageStatus || '') === 'Active' && !assignmentHasEnded(a),
      );
      if (active.length === 0) continue;
      count += 1;
      for (const a of active) {
        const hrs = Number(a.hoursAllocated);
        if (!Number.isFinite(hrs) || hrs <= 0) continue;
        const days = plantDaysPerWeek;
        const unit = String(a.hoursUnit || '').toLowerCase();
        if (unit === 'per shift') hoursPerDay += hrs * shiftsPerDay;
        else if (unit === 'per week') hoursPerDay += hrs / days;
        else if (unit === 'per month') hoursPerDay += hrs / (days * 4.33);
        else hoursPerDay += hrs; // Per Day / Other → use literal
      }
    }
    return { count, hoursPerDay };
  }, [
    plant?.PlantCode,
    plant?.ShiftsWeekdays,
    plant?.ShiftsSaturdays,
    plant?.ShiftsSundays,
    plant?.ShiftsPerDay,
    plant?.crewShifts,
    parts,
    suppliersByCode,
    directSuppliers,
  ]);

  if (loading || scopeLoading) return <Loading />;
  // Block plant-scoped users (Regional / Liaison Managers) from opening a
  // plant that isn't assigned to them — even via a direct URL.
  if (scoped && !allows(plantCode)) {
    return (
      <div className="card">
        <div className="card-title">Plant Not Available</div>
        <p>
          You can only view plants assigned to you. If you think you should have
          access to <strong>{plantCode}</strong>, contact a Director to update
          your plant assignment.
        </p>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/plants')}>
          Back to Plants
        </button>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load This Plant</div>
        <p>{error.message}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  if (!plant) {
    return (
      <div className="card">
        <div className="card-title">Plant Not Found</div>
        <p>
          No plant with code <strong>{plantCode}</strong>.
        </p>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/plants')}>
          Back to Plants
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
            <Link to="/plants">Plants</Link> / {plant.PlantCode}
          </div>
          <h1 style={{ margin: '4px 0 0' }}>{plant.Title}</h1>
        </div>
        <div className="actions">
          <button className="btn btn-outline" onClick={() => navigate('/plants')}>
            ← Plants
          </button>
          {canEditPlants && (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title">Plant Info</div>
        {/* Column widths tuned so OEM lines up under Vehicle Model
            and Address lines up under Program in the Vehicles table
            directly below. Vehicles table cols: 120 / 200 / 1fr /
            120 (Status). The grid here mirrors the first three so
            the header/value pairs read down the page in the same
            tracks. */}
        <div
          style={{
            display: 'grid',
            // Desktop: Plant Code / OEM / Address keep their original tracks
            // (120 / 200 / 1fr) so they still line up with the Vehicles
            // table below; Plant Type rides on the end in its own fixed
            // column so the whole row reads on one line. On phones those
            // fixed tracks squeeze the address to one word per line, so we
            // drop to two columns and give Address its own full-width row.
            gridTemplateColumns: isMobile ? '1fr 1fr' : '120px 200px 1fr 140px',
            gap: isMobile ? 12 : 0,
          }}
        >
          <Field label="Plant Code" value={plant.PlantCode} />
          <Field label="OEM" value={plant.OEM} />
          <Field
            label="Address"
            full={isMobile}
            value={
              formatAddressLine({
                street: plant.AddressStreet || plant.Address,
                city: plant.City,
                state: plant.State,
                postalCode: plant.PostalCode,
                country: plant.Country,
              }) || ''
            }
          />
          <Field label="Plant Type" value={plant.PlantType} />
        </div>
      </div>

      <div className="card">
        <div className="card-title">Vehicles</div>
        {plant.vehicles?.length ? (
          <table className="mini-table">
            <thead>
              <tr>
                {/* Column widths match the Plant Info card above so
                    Vehicle Make sits under Plant Code, Vehicle Model
                    sits under OEM, and Program sits under Address. */}
                <th style={{ width: 120 }}>Vehicle Make</th>
                <th style={{ width: 200 }}>Vehicle Model</th>
                <th>Program</th>
                <th style={{ width: 120 }}>SOP Date</th>
                <th style={{ width: 120 }}>EOP Date</th>
                <th style={{ width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {plant.vehicles.map((v, i) => (
                <tr key={i}>
                  <td>{v.make || '—'}</td>
                  <td>{v.model || v.name || '—'}</td>
                  <td>{v.program || v.modelYear || '—'}</td>
                  <td>{formatClosureDate(v.sopDate)}</td>
                  <td>{formatClosureDate(v.eopDate)}</td>
                  <td>
                    {v.status ? (
                      <span className={`badge ${statusBadge(v.status)}`}>
                        {v.status}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No vehicles listed yet.</p>
        )}
        {/* Schedule Notes — production-schedule context covering all
            vehicles at this plant. Only renders when something's
            been written; an empty string keeps the card tight. */}
        {plant.VehicleScheduleNotes && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'var(--brand-muted)',
                marginBottom: 4,
              }}
            >
              Schedule Notes
            </div>
            <div
              style={{
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                color: 'var(--brand-ink)',
              }}
            >
              {plant.VehicleScheduleNotes}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title">Proxemics Team at This Plant</div>
        {plantTeam.coos.length === 0 &&
        plantTeam.rms.length === 0 &&
        plantTeam.lms.length === 0 &&
        plantTeam.liaisons.length === 0 ? (
          <p className="muted">
            No team members linked. They'll appear automatically when a team
            member is linked on the <Link to="/team">Proxemics Team</Link> page.
          </p>
        ) : (
          <>
            <DetailGrid>
              {/* COO row renders first — the C-suite tier sits above the
                  Regional Manager in the org structure on every plant. */}
              {plantTeam.coos.length > 0 && (
                <Field
                  label={plantTeam.coos.length === 1 ? 'COO' : 'COOs'}
                  value={renderMemberLinks(plantTeam.coos)}
                  full
                />
              )}
              <Field
                label={`Regional Manager${plantTeam.rms.length === 1 ? '' : 's'}`}
                value={renderMemberLinks(plantTeam.rms)}
              />
              <Field
                label={`Liaison Manager${plantTeam.lms.length === 1 ? '' : 's'}`}
                value={renderMemberLinks(plantTeam.lms)}
              />
              {/* Liaisons get their own full-width row beneath the
                  Regional Manager / Liaison Manager pair so every
                  liaison badge stays on a single line instead of
                  wrapping into the narrow third column. Rendered as
                  badge pills (rather than comma-separated text) because
                  a plant typically has several liaisons and badges keep
                  the names visually scannable when there are many. */}
              {plantTeam.liaisons.length > 0 && (
                <Field
                  label="Liaisons"
                  full
                  value={
                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6 }}>
                      {plantTeam.liaisons.map((m) => (
                        <Link
                          key={m.id}
                          to={`/team/${m.id}`}
                          className="badge"
                          style={{
                            textDecoration: 'none',
                            background: '#e5e7eb',
                            color: '#374151',
                            borderColor: '#e5e7eb',
                          }}
                        >
                          {fullName(m)}
                        </Link>
                      ))}
                    </div>
                  }
                />
              )}
            </DetailGrid>
          </>
        )}
      </div>

      {/* Crew Shifts card — three day-of-week shift counts followed
          by free-form Shift Notes. Renders whenever any of the new
          fields has a value OR when legacy crewShifts / ShiftsPerDay
          data exists, so plants saved before the split still show
          their info until they're re-saved. */}
      {(plant.ShiftsWeekdays ||
        plant.ShiftsSaturdays ||
        plant.ShiftsSundays ||
        plant.ShiftNotes ||
        plant.crewShifts?.length > 0 ||
        plant.ShiftsPerDay) && (
        <div className="card">
          <div className="card-title">Crew Shifts</div>
          <DetailGrid>
            <Field
              label="Shifts on Weekdays"
              value={plant.ShiftsWeekdays || ''}
            />
            <Field
              label="Shifts on Saturdays"
              value={plant.ShiftsSaturdays || ''}
            />
            <Field
              label="Shifts on Sundays"
              value={plant.ShiftsSundays || ''}
            />
          </DetailGrid>
          {plant.ShiftNotes && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'var(--brand-muted)',
                  marginBottom: 4,
                }}
              >
                Shift Notes
              </div>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {plant.ShiftNotes}
              </p>
            </div>
          )}
          {/* Legacy fallback display — shown only for plants that
              still have pre-split data (ShiftsPerDay or crewShifts)
              and haven't been re-saved into the new fields yet. */}
          {!plant.ShiftsWeekdays &&
            !plant.ShiftsSaturdays &&
            !plant.ShiftsSundays &&
            plant.ShiftsPerDay && (
              <p
                style={{
                  margin: '12px 0 0',
                  fontSize: 12,
                  color: 'var(--brand-muted)',
                }}
              >
                Legacy data — Shift/s per Day: <strong>{plant.ShiftsPerDay}</strong>
                {plant.crewShifts?.length > 0 && (
                  <>
                    {'. Schedule: '}
                    {plant.crewShifts
                      .map((s) =>
                        [s.shift, s.schedule].filter(Boolean).join(' — '),
                      )
                      .filter(Boolean)
                      .join('; ')}
                  </>
                )}
                {'. Edit the plant to record the new weekday / weekend split.'}
              </p>
            )}
        </div>
      )}


      {/* Two stat tiles sitting directly above the Suppliers card.
          Active Suppliers = number of plantAssignments at this plant
          whose coverageStatus is "Active". Active Hours / Day = the
          sum of those suppliers' allocated hours, normalised to a
          per-day equivalent (weekly hours ÷ coverage-days, monthly
          hours ÷ (coverage-days × 4.33), etc. — see the
          activeSupplierStats memo above). Hours are rounded to 1
          decimal for readability and any conversion lossiness; the
          memo keeps the raw float for sortable / export use later.
          No marginBottom — the row sits flush against the Suppliers
          card so the visual gap matches the rest of the page's
          card-to-card spacing. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 16,
        }}
      >
        <StatTile
          label="Active Suppliers"
          value={activeSupplierStats.count}
        />
        <StatTile
          label="Active Hours / Day"
          value={
            activeSupplierStats.hoursPerDay
              ? Number(activeSupplierStats.hoursPerDay.toFixed(1)).toString()
              : '0'
          }
        />
      </div>

      <div className="card">
        <div className="card-title">Suppliers</div>
        <SuppliersSortableTable
          plant={plant}
          parts={parts}
          suppliersByCode={suppliersByCode}
          directSuppliers={directSuppliers}
        />
      </div>

      <div className="card">
        <div className="card-title">Parts Received</div>
        {parts.length ? (
          <table className="mini-table">
            <thead>
              <tr>
                <th>Part name</th>
                <th>Part #/s</th>
                <th>Part Description</th>
                <th>Supplier</th>
                <th>For vehicle(s)</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => {
                // Pull the vehicles tagged specifically for this plant.
                const vehiclesForPlant = (p.plantVehicles || [])
                  .filter((pv) => pv.plantCode === plant.PlantCode)
                  .map((pv) => pv.vehicle)
                  .filter(Boolean);
                // List every part type's Part # + Description (matching the
                // Parts page), falling back to the legacy record-level
                // PartNumber / Description for parts with no variants yet.
                let partRows = (p.variants || [])
                  .map((v) => ({
                    num: (v.partNumber || '').trim(),
                    desc: (v.description || '').trim(),
                  }))
                  .filter((e) => e.num || e.desc);
                if (partRows.length === 0) {
                  const num = (p.PartNumber || '').trim();
                  const desc = (p.Description || '').trim();
                  if (num || desc) partRows = [{ num, desc }];
                }
                // Render one column (num | desc) with a row per part type,
                // keeping a "—" placeholder for blanks so the Part # and
                // Part Description columns stay aligned line-for-line.
                const stackCol = (key) => {
                  if (partRows.length === 0)
                    return <span className="muted">—</span>;
                  if (partRows.length === 1)
                    return partRows[0][key] || '—';
                  return (
                    <div
                      style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {partRows.map((e, i) => (
                        <span key={i}>{e[key] || '—'}</span>
                      ))}
                    </div>
                  );
                };
                return (
                  <tr
                    key={p.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      p.PartCode &&
                      navigate(`/parts/${encodeURIComponent(p.PartCode)}`)
                    }
                  >
                    <td>{p.Title || '—'}</td>
                    <td>{stackCol('num')}</td>
                    <td>{stackCol('desc')}</td>
                    <td>
                      {p.SupplierCode
                        ? suppliersByCode[p.SupplierCode]?.Title || p.SupplierCode
                        : '—'}
                    </td>
                    <td>
                      {vehiclesForPlant.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {vehiclesForPlant.map((v) => (
                            <span key={v} className="badge vehicle">{v}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="muted">All / unspecified</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="muted">
            No parts linked. They'll appear automatically when a part is
            linked on the <Link to="/parts">Parts</Link> page.
          </p>
        )}
      </div>

      {(plant.ppe?.vestColor ||
        plant.ppe?.safetyGlasses ||
        plant.ppe?.safetyShoes ||
        plant.ppe?.sleeves ||
        plant.ppe?.gloves ||
        plant.ppe?.scratchGuards) && (
        <div className="card">
          <div className="card-title">Safety Equipment (PPE)</div>
          {/* Force all six PPE fields onto one row. The default
              DetailGrid auto-fits at min-width 220px, which would
              wrap to 2–3 rows on the typical content width.
              minmax(0, 1fr) lets each cell shrink below its content
              width so all six always fit, with labels and values
              wrapping inside their cells if a viewport gets really
              narrow. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            <Field label="Vest Color" value={plant.ppe.vestColor} />
            <Field label="Safety Glasses" value={plant.ppe.safetyGlasses} />
            <Field label="Safety Shoes" value={plant.ppe.safetyShoes} />
            <Field label="Sleeves" value={plant.ppe.sleeves} />
            <Field label="Gloves" value={plant.ppe.gloves} />
            <Field
              label="Scratch Guards / ISD Protection"
              value={plant.ppe.scratchGuards}
            />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title">Plant Closures</div>
        {plant.plantClosures?.length ? (
          <table className="mini-table">
            <thead>
              <tr>
                <th>Start</th>
                <th>End</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {[...plant.plantClosures]
                .sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''))
                .map((c, i) => (
                  <tr key={i}>
                    <td>{formatClosureDate(c.startDate)}</td>
                    <td>{formatClosureDate(c.endDate)}</td>
                    <td>{c.reason || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No closures scheduled.</p>
        )}
      </div>

      {plant.plantContacts?.length > 0 && (
        <div className="card">
          <div className="card-title">OEM Plant Contacts</div>
          <table className="mini-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Title</th>
                <th>Email</th>
                <th>Cell Phone</th>
                {!isMobile && <th>Office Phone</th>}
              </tr>
            </thead>
            <tbody>
              {plant.plantContacts.map((c, i) => (
                <tr key={i}>
                  <td>{c.name || '—'}</td>
                  <td>{c.company || '—'}</td>
                  <td>{c.title || '—'}</td>
                  <td>{c.email || '—'}</td>
                  <td>{formatPhone(c.phone) || '—'}</td>
                  {!isMobile && <td>{formatPhone(c.officePhone) || '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Array.isArray(plant.sequencingFacilities) &&
        plant.sequencingFacilities.filter(
          (f) => f && (f.name || f.address || f.notes),
        ).length > 0 && (
          <div className="card">
            <div className="card-title">Sequencing Facilities</div>
            {plant.sequencingFacilities
              .filter((f) => f && (f.name || f.address || f.notes))
              .map((f, i, arr) => (
                <div
                  key={i}
                  style={{
                    paddingTop: i === 0 ? 0 : 12,
                    paddingBottom: i === arr.length - 1 ? 0 : 12,
                    borderTop:
                      i === 0 ? 'none' : '1px solid var(--brand-border)',
                  }}
                >
                  <DetailGrid>
                    <Field label="Name" value={f.name} />
                    <Field label="Address" value={f.address} full />
                    <Field label="Notes" value={f.notes} full />
                  </DetailGrid>
                </div>
              ))}
          </div>
        )}

      {plant.Notes && (
        <div className="card">
          <div className="card-title">Notes</div>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{plant.Notes}</p>
        </div>
      )}

      {/* Save as PDF generates a branded, structured PDF (jsPDF) that
          mirrors the sections on this page. Users can still print from
          the resulting PDF via their PDF viewer — no need for a
          separate Print button. */}
      <div
        className="no-print"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 20,
        }}
      >
        <button
          className="btn btn-primary"
          onClick={() =>
            savePlantPdf(plant, plantTeam, {
              parts,
              suppliersByCode,
              directSuppliers,
            })
          }
        >
          Save as PDF
        </button>
      </div>

      {/* ---- Delete plant ----
          Director-only (canDeletePlants, off on mobile). Same layout as the
          Supplier / Team Member delete: red-outlined button on the left,
          muted guidance on the right, at the very bottom of the page so it's
          hard to hit by accident while scanning the record. */}
      {canDeletePlants && (
        <div
          className="no-print"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginTop: 16,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="btn btn-outline"
            onClick={onDelete}
            style={{
              color: 'var(--brand-danger)',
              borderColor: 'var(--brand-danger)',
              flexShrink: 0,
            }}
          >
            Delete Plant
          </button>
          <span className="muted" style={{ fontSize: 12, lineHeight: 1.3 }}>
            Only delete a plant if it was added by mistake. Parts, suppliers,
            and team members that point at this plant code are not removed.
          </span>
        </div>
      )}

      <Modal
        open={editing}
        onClose={() => {
          if (saving) return;
          setEditing(false);
          setSaveError(null);
        }}
        title={`Edit ${plant.Title}`}
      >
        {editing && (
          <>
            {saveError && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  background: '#fde8e8',
                  color: '#b91c1c',
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                <strong>Couldn't save:</strong> {saveError}
              </div>
            )}
            <PlantForm
              initial={plant}
              saving={saving}
              onSave={onSave}
              onCancel={() => {
                setEditing(false);
                setSaveError(null);
              }}
            />
          </>
        )}
      </Modal>
    </>
  );
}

const STATUS_BADGE = {
  'Pre-Launch': 'warning',
  Launch: 'primary',
  Production: 'success',
  'Wind-Down': 'neutral',
  Closed: 'neutral',
};
function statusBadge(status) {
  return STATUS_BADGE[status] || 'neutral';
}

/**
 * Suppliers table on the Plant detail page — sortable columns
 * (Supplier / Part/s / Rate / Coverage Hours / Coverage Unit /
 * Status). Two data sources feed the list:
 *   1. Parts on this plant (SupplierCode column).
 *   2. Suppliers whose Plant Locations & Coverage entry names this
 *      plant directly — these can show up before any parts are
 *      linked, so they're merged in.
 * Default sort matches the previous static order: alphabetical by
 * supplier name, with name-less orphan rows pinned to the bottom.
 */

// Compact "stat tile" used above the Suppliers card to surface
// at-a-glance totals (active supplier count, active hours/day). Looks
// like a sibling of a regular card: same border / radius / padding,
// just with a big numeric value on top of a muted label. Kept tiny
// and prop-driven so we can reuse it on other plant-level summaries
// later without spinning up a separate file.
function StatTile({ label, value }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid var(--brand-border, #e5e7eb)',
        borderRadius: 10,
        // Asymmetric vertical padding to compensate for the digit's
        // glyph metrics: with lineHeight: 1 the "9" / "37" sit flush
        // against the top of their line box, but the all-caps label
        // has no descenders so it floats high in its line box —
        // leaving more visual whitespace below than above when
        // padding is uniform. Tightening the bottom to 14px (vs the
        // 18px sides) brings the label's visual bottom edge in line
        // with the top.
        padding: '18px 18px 14px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          // Brand orange so the metric reads as a highlighted KPI
          // against the muted label below it — matches the rest of
          // the portal's primary accent (buttons, hover borders,
          // primary badge pills).
          color: 'var(--brand-primary)',
          // Line height 1 (instead of 1.1) trims the invisible leading
          // above the digit glyph so the "9" / "37" sit visually at
          // the same distance from the top edge as the label sits
          // from the bottom edge.
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--brand-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          lineHeight: 1,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function SuppliersSortableTable({
  plant,
  parts,
  suppliersByCode,
  directSuppliers,
}) {
  const isMobile = useIsMobile();
  // Hide the supplier's billing Rate from Liaison Managers (per Jillian,
  // Aug 2026) — they don't need to see it on the plant page.
  const { isLiaisonManager } = usePermissions();
  // Sep 2026: a Liaison Manager only sees the suppliers they're responsible
  // for at this plant (see utils/supplierScope.js). Unscoped roles pass.
  const { allowsSupplierAtPlant } = usePlantScope();
  const rows = useMemo(() => {
    const partsCodes = (parts || [])
      .map((pt) => pt.SupplierCode)
      .filter(Boolean);
    const directCodes = (directSuppliers || [])
      .map((s) => s.SupplierCode)
      .filter(Boolean);
    const mergedCodes = Array.from(
      new Set([...partsCodes, ...directCodes]),
    ).filter(
      (code) =>
        !isLiaisonManager ||
        allowsSupplierAtPlant(plant?.PlantCode, code),
    );
    const lookup = { ...(suppliersByCode || {}) };
    for (const s of directSuppliers || []) {
      if (s.SupplierCode && !lookup[s.SupplierCode]) {
        lookup[s.SupplierCode] = s;
      }
    }
    const plantCode = (plant?.PlantCode || '').toUpperCase();
    return mergedCodes
      .flatMap((code) => {
        const supplier = lookup[code];
        // One table row per (supplier, service) — a supplier billing two
        // services at this plant (e.g. Standard Coverage + Lineside
        // Support) shows two rows, each with its own rate and hours. A
        // supplier with no assignment here still gets one placeholder row.
        const atPlant = assignmentsAtPlant(supplier, plantCode);
        const assignments = atPlant.length ? atPlant : [null];
        return assignments.map((assignment) => ({ code, supplier, assignment }));
      })
      .map(({ code, supplier, assignment }) => {
        const supplierParts = (parts || [])
          .filter(
            (pt) =>
              String(pt.SupplierCode || '').toUpperCase() ===
              String(code).toUpperCase(),
          )
          .sort((a, b) => (a.Title || '').localeCompare(b.Title || ''));
        const a = assignment;
        const hourlyRateNum =
          a && a.hourlyRate != null && a.hourlyRate !== ''
            ? Number(a.hourlyRate)
            : null;
        const hoursNum =
          a && a.hoursAllocated != null && a.hoursAllocated !== ''
            ? Number(a.hoursAllocated)
            : null;
        return {
          code,
          // Stable key + non-default service label for the table.
          rowKey: `${code}|${a ? String(a.service || '') : ''}`,
          service: a ? serviceLabel(a) : '',
          ended: a ? assignmentHasEnded(a) : false,
          name: supplier?.Title || null,
          assignment,
          parts: supplierParts,
          // Pre-computed sortable values so the columns can sort on
          // numeric / string primitives even when the cell renders a
          // composite (e.g. "$45.00/hr" → 45 for sort).
          hourlyRateNum,
          hoursNum,
          hoursUnit: (a && a.hoursUnit) || '',
          coverageStatus: (a && a.coverageStatus) || '',
          partsLabel: supplierParts
            .map((pt) => pt.Title || pt.PartCode || '')
            .filter(Boolean)
            .join(', '),
        };
      });
  }, [plant, parts, suppliersByCode, directSuppliers, isLiaisonManager, allowsSupplierAtPlant]);

  // Default sort: coverage status in lifecycle order, then supplier
  // name A→Z within each status tier, with orphan codes (no name on
  // file) sinking to the bottom of each tier. The status ordering
  // matches how Directors think about a supplier's lifecycle —
  // Active is what's running now, then On Call (active but standing
  // by), then anything in flight (Proposal Out, Prospecting), then
  // the wound-down statuses (Paused, Suspended, Closed, Declined).
  // Anything outside this map (legacy/empty status) sinks below
  // Declined. Implemented as a composite string key — leading digit
  // for status rank, then `0|<name>` for named rows and `1|<code>`
  // for orphans. Clicking another column header still re-sorts on
  // that column normally; this only affects the initial state.
  const STATUS_RANK = {
    Active: '0',
    'On Call': '1',
    'Proposal Out': '2',
    Prospecting: '3',
    Paused: '4',
    Suspended: '5',
    Closed: '6',
    Declined: '7',
  };
  const sortConfig = useMemo(
    () => ({
      name: (r) => {
        const statusTier = STATUS_RANK[r.coverageStatus] ?? '9';
        const nameTier =
          r.name && r.name.trim()
            ? `0|${r.name.toLowerCase()}`
            : `1|${String(r.code).toLowerCase()}`;
        return `${statusTier}|${nameTier}`;
      },
      partsLabel: (r) => r.partsLabel || '',
      hourlyRateNum: (r) =>
        r.hourlyRateNum == null ? -Infinity : r.hourlyRateNum,
      hoursNum: (r) => (r.hoursNum == null ? -Infinity : r.hoursNum),
      hoursUnit: (r) => r.hoursUnit || '',
      coverageStatus: (r) => r.coverageStatus || '',
    }),
    [],
  );
  const [sortedRows, sort, onSort] = useSortedRows(rows, sortConfig, {
    key: 'name',
    dir: 'asc',
  });

  if (rows.length === 0) {
    return (
      <p className="muted">
        No suppliers linked. They'll appear automatically when a supplier
        is linked on the <Link to="/suppliers">Suppliers</Link> page.
      </p>
    );
  }

  return (
    <table className="mini-table">
      <thead>
        <tr>
          {/* Row-number column — purely visual position counter, not
              sortable. Renders the supplier's place in the current
              sort order, so re-sorting renumbers from the top. Kept
              narrow (40px) and muted so it reads as a gutter rather
              than data. */}
          <th
            style={{
              width: 40,
              textAlign: 'right',
              color: 'var(--brand-muted)',
              fontWeight: 600,
            }}
          >
            #
          </th>
          {/* Supplier column is width-constrained so the flexible
              Part/s column to its right has room to spread out for
              suppliers carrying several part numbers. ~260px keeps
              longer supplier names from wrapping while still leaving
              the Part/s column comfortable. */}
          <SortableTh
            sortKey="name"
            currentSort={sort}
            onSort={onSort}
            width={260}
          >
            Supplier
          </SortableTh>
          {!isMobile && (
            <SortableTh
              sortKey="partsLabel"
              currentSort={sort}
              onSort={onSort}
            >
              Part/s
            </SortableTh>
          )}
          {!isMobile && !isLiaisonManager && (
            <SortableTh
              sortKey="hourlyRateNum"
              currentSort={sort}
              onSort={onSort}
              width={110}
            >
              Rate
            </SortableTh>
          )}
          <SortableTh
            sortKey="hoursNum"
            currentSort={sort}
            onSort={onSort}
            width={130}
          >
            Coverage Hours
          </SortableTh>
          <SortableTh
            sortKey="hoursUnit"
            currentSort={sort}
            onSort={onSort}
            width={140}
          >
            Coverage Unit
          </SortableTh>
          <SortableTh
            sortKey="coverageStatus"
            currentSort={sort}
            onSort={onSort}
            width={120}
          >
            Status
          </SortableTh>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((r, idx) => {
          const a = r.assignment;
          const rate =
            a && a.hourlyRate != null && a.hourlyRate !== ''
              ? `$${Number(a.hourlyRate).toFixed(2)}/hr`
              : '—';
          const hours =
            a && a.hoursAllocated != null && a.hoursAllocated !== ''
              ? Number(a.hoursAllocated)
              : '—';
          const unit = (a && a.hoursUnit) || '—';
          const status = (a && a.coverageStatus) || null;
          return (
            <tr key={r.rowKey || r.code}>
              {/* Row-number gutter — matches the '#' header. Muted
                  so the eye lands on the supplier name rather than
                  the count. */}
              <td
                style={{
                  textAlign: 'right',
                  color: 'var(--brand-muted)',
                }}
              >
                {idx + 1}
              </td>
              <td>
                {r.name ? (
                  <Link
                    to={`/suppliers/${encodeURIComponent(r.code)}`}
                    style={{ color: 'var(--brand-ink)' }}
                  >
                    {/* Account number first, then the supplier
                        name — Directors scan by account # in
                        this list, so leading with it makes
                        lookup faster. Muted code so the name
                        still reads as the dominant label. */}
                    <span className="muted" style={{ marginRight: 6 }}>
                      {r.code}
                    </span>
                    {r.name}
                  </Link>
                ) : (
                  <span>
                    {r.code}{' '}
                    <em className="muted" style={{ fontSize: 11 }}>
                      (not in Suppliers list)
                    </em>
                  </span>
                )}
                {/* Non-default service name (e.g. "Lineside Support") so
                    two rows for the same supplier are distinguishable. */}
                {r.service && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {r.service}
                  </div>
                )}
                {r.ended && (
                  <span
                    className="badge"
                    title="Coverage end date has passed"
                    style={{
                      marginLeft: 6,
                      background: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: 11,
                    }}
                  >
                    Ended
                  </span>
                )}
              </td>
              {!isMobile && (
                <td>
                  {r.parts.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    r.parts.map((pt, idx) => (
                      <span key={pt.id || pt.PartCode || idx}>
                        {pt.PartCode ? (
                          <Link
                            to={`/parts/${encodeURIComponent(pt.PartCode)}`}
                            style={{ color: 'var(--brand-ink)' }}
                          >
                            {pt.Title || pt.PartCode}
                          </Link>
                        ) : (
                          pt.Title || '—'
                        )}
                        {idx < r.parts.length - 1 ? ', ' : ''}
                      </span>
                    ))
                  )}
                </td>
              )}
              {!isMobile && !isLiaisonManager && <td>{rate}</td>}
              <td>{hours}</td>
              <td>{unit}</td>
              <td>
                {status ? (
                  <CoverageStatusBadge status={status} />
                ) : (
                  '—'
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Small colored pill for a supplier's per-plant coverage lifecycle.
// Mirrors the palette from SupplierDetail's CoverageStatusBadge so a
// supplier showing "Active" on its own page also shows green here.
// Blue Prospecting → purple Proposal Out → green Active → teal On
// Call → amber Paused → orange Suspended → red Closed → gray
// Declined.
function CoverageStatusBadge({ status }) {
  const palette = {
    Prospecting: { bg: 'rgba(30, 64, 175, 0.1)', fg: '#1e40af' },
    'Proposal Out': { bg: 'rgba(126, 34, 206, 0.1)', fg: '#7e22ce' },
    Active: { bg: 'rgba(22, 163, 74, 0.1)', fg: 'var(--brand-success, #16a34a)' },
    'On Call': { bg: 'rgba(13, 148, 136, 0.12)', fg: '#0d9488' },
    Paused: { bg: 'rgba(202, 138, 4, 0.1)', fg: '#ca8a04' },
    Suspended: { bg: 'rgba(234, 88, 12, 0.12)', fg: '#ea580c' },
    Closed: { bg: 'rgba(220, 38, 38, 0.1)', fg: 'var(--brand-danger)' },
    Declined: { bg: 'rgba(100, 116, 139, 0.12)', fg: '#475569' },
  };
  const colors = palette[status] || palette.Active;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 999,
        background: colors.bg,
        color: colors.fg,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {status}
    </span>
  );
}

function DetailGrid({ children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, value, full }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value || <span className="muted">—</span>}</div>
    </div>
  );
}

// Display-preferred name (nickname when set, else FirstName + LastName, else
// the legacy Title). Used by the plant team badges and renderMemberLinks.
const fullName = displayFullName;

// Closure dates are stored as ISO date strings (YYYY-MM-DD). Render them in a
// friendly US-style format without timezone drift: parsing the bare string with
// new Date() can shift a day in negative-UTC zones, so we split manually.
function formatClosureDate(iso) {
  if (!iso) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Render a list of team members as comma-separated links to their detail
// pages. Returns null (not an empty string) when the list is empty so the
// caller can fall back to the plant's stored text field.
function renderMemberLinks(members) {
  if (!members || members.length === 0) return null;
  return members.map((m, i) => (
    <span key={m.id}>
      {i > 0 && ', '}
      <Link to={`/team/${m.id}`}>{fullName(m)}</Link>
    </span>
  ));
}

/**
 * Return the list of contact rows that appear in the new list but not
 * the old one. Matching is by normalised name (case-insensitive,
 * trimmed) — if someone fixes a typo on an existing row we don't want to
 * log it as an Add. Blank-named rows are skipped because they're usually
 * placeholder rows the user added but didn't fill in yet.
 *
 * Mirrors the helper in SupplierDetail.jsx. Duplicated here (rather than
 * extracted) to keep each detail page self-contained — same pattern the
 * existing formatting helpers follow.
 */
function diffAddedContacts(oldList, newList) {
  const norm = (s) => String(s || '').trim().toLowerCase();
  const oldNames = new Set(
    (oldList || []).map((c) => norm(c.name)).filter(Boolean),
  );
  return (newList || []).filter((c) => {
    const n = norm(c.name);
    if (!n) return false;
    return !oldNames.has(n);
  });
}
